import React, { useState, useEffect } from 'react';
import { BarChart3, LogOut, ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../hooks/useNotifications';
import UserAvatar from './UserAvatar';
import DolarIndicator from './DolarIndicator';
import NotificationBadge from './NotificationBadge';
import NotificationCenter from './NotificationCenter';

const Header = ({ carCount, onToggleStats, showStats, currentSection, onSectionChange }) => {
  const { userProfile, logout } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const { unreadCount, notifications, loading, fetchNotifications, markAsRead } = useNotifications();
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL || '/api';
    fetch(`${apiUrl}/last-update`)
      .then(res => res.json())
      .then(data => {
        if (data.last_update) {
          setLastUpdate(data.last_update);
        }
      })
      .catch(() => {});
  }, []);

  const getSectionDescription = () => {
    switch (currentSection) {
      case 'market':
        return 'Análisis de precios y tendencias del mercado automotriz';
      case 'prospection':
        return 'Gestión de leads y seguimiento de clientes potenciales';
      case 'motos':
        return 'Análisis de precios y tendencias del mercado de motos';
      case 'summary':
        return 'Resumen semanal del mercado por segmento';
      default:
        return '';
    }
  };

  const navItems = [
    { key: 'market', label: 'Mercado' },
    { key: 'motos', label: 'Motos' },
    { key: 'seguimiento', label: 'Seguimiento' },
    { key: 'summary', label: 'Resumen', shortLabel: 'KPIs' },
  ];

  return (
    <header className="mb-10">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between py-6">
        {/* Logo */}
        <div className="mb-6 lg:mb-0">
          <div className="flex items-center gap-1.5">
            <h1 className="text-2xl font-semibold text-[#e8e4de] tracking-tight">
              CarTracker
            </h1>
            <span className="w-2 h-2 rounded-full bg-[#7b8fa8] mt-1"></span>
          </div>
          <p className="text-[11px] uppercase tracking-widest text-[#8a8577] mt-2">
            {getSectionDescription()}
          </p>
          {lastUpdate && (
            <p className="text-[10px] text-[#6b6560] mt-1">
              Última actualización: {new Date(lastUpdate).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>

        {/* Right side */}
        <div className="flex items-center gap-6">
          {/* Car count */}
          {currentSection === 'market' && (
            <div className="flex items-center gap-5">
              <div className="text-right">
                <p className="text-2xl font-mono font-semibold text-[#e8e4de]">{carCount}</p>
                <p className="text-[10px] uppercase tracking-wide text-[#8a8577] hidden lg:block">en seguimiento</p>
              </div>
              <div className="hidden sm:block">
                <DolarIndicator />
              </div>
            </div>
          )}

          {/* Stats toggle */}
          {(currentSection === 'market' || currentSection === 'motos') && (
            <button
              onClick={onToggleStats}
              className={`flex items-center gap-1.5 text-sm transition-colors duration-150 ${
                showStats ? 'text-[#7b8fa8]' : 'text-[#8a8577] hover:text-[#e8e4de]'
              }`}
            >
              <BarChart3 className="w-4 h-4" />
              <span>{showStats ? 'Ocultar' : 'Stats'}</span>
            </button>
          )}

          {/* Notifications */}
          {userProfile && (
            <div className="relative">
              <NotificationBadge
                count={unreadCount}
                onClick={() => { setShowNotifications(!showNotifications); fetchNotifications(1); }}
              />
              {showNotifications && (
                <div className="absolute top-full right-0 mt-2 z-50">
                  <NotificationCenter
                    notifications={notifications}
                    loading={loading}
                    onClose={() => setShowNotifications(false)}
                    onViewVehicle={() => {}}
                    onMarkAsRead={markAsRead}
                    onLoadMore={() => fetchNotifications()}
                  />
                </div>
              )}
            </div>
          )}

          {/* User */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 transition-colors duration-150 hover:opacity-80"
            >
              <UserAvatar userProfile={userProfile} size="sm" />
              <span className="text-sm text-[#e8e4de] hidden sm:block">
                {userProfile?.username || 'Usuario'}
              </span>
              <ChevronDown className={`w-3.5 h-3.5 text-[#8a8577] transition-transform duration-150 hidden sm:block ${showUserMenu ? 'rotate-180' : ''}`} />
            </button>

            {showUserMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                <div className="absolute top-full right-0 mt-2 w-48 bg-[#1e1d1b] border border-[#2a2926] rounded-md shadow-xl z-50">
                  <div className="px-3 py-2.5 border-b border-[#2a2926]">
                    <p className="text-sm text-[#e8e4de]">{userProfile?.username}</p>
                    <p className="text-xs text-[#8a8577]">{userProfile?.email}</p>
                  </div>
                  <div className="p-1">
                    <button
                      onClick={() => { logout(); setShowUserMenu(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#8a8577] hover:text-[#e8e4de] rounded transition-colors duration-150"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Cerrar Sesión</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex items-center gap-6 overflow-x-auto pb-3 -mx-4 px-4">
        {navItems.map(({ key, label, shortLabel }) => (
          <button
            key={key}
            onClick={() => onSectionChange(key)}
            className={`relative text-sm whitespace-nowrap pb-2 transition-colors duration-150 ${
              currentSection === key
                ? 'text-[#e8e4de] font-medium'
                : 'text-[#8a8577] hover:text-[#e8e4de]'
            }`}
          >
            <span className="hidden sm:inline">{label}</span>
            <span className="sm:hidden">{shortLabel || label}</span>
            {currentSection === key && (
              <span className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#7b8fa8]"></span>
            )}
          </button>
        ))}
      </nav>

      {/* Bottom border */}
      <div className="border-b border-[#2a2926]"></div>
    </header>
  );
};

export default Header;
